import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { theme } from "../../../utils/global-styles/theme";
import Link from "../../shared/Link/Link";
import styled from "@emotion/styled";
import RightGoToArrow from "../../CourseCard/Icons/RightGoToArrow";

import {
  determineProductType,
  determineProductPath,
} from "../../../utils/services/methods";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

const DualColumnCard = ({
  languageCode,
  slug,
  title,
  cta,
  shortDescription,
  courseCost,
  courseDuration,
  courseType,
  courseAvailability,
  courseImage,
  gridColumnValue,
  courseIcon,
  darkImageOverlayText,
  isCourseDetailPage,
}) => {
  const DoubleCardImageBg = styled(GatsbyImage)`
      grid-area: 1 / ${gridColumnValue} / span 1 / span 2;
      height: 360px;
      display: grid;
    `,
    DoubleCardWrapper = styled(Link)`
      grid-area: 1 / ${gridColumnValue} / span 1 / span 2;
      height: 360px;
      z-index: 1;
      display: grid;
      grid-template: 1fr 3fr 1fr / 50% 50%;
      overflow: hidden;
      &:hover {
        transition: 0.2s ease-in-out;
        cursor: pointer;
        h4,
        p {
          color: ${theme.colors.main};
          border-top-color: ${theme.colors.main};
          text-overflow: ellipsis;
        }
        svg {
          path {
            fill: ${theme.colors.main};
          }
        }
        h5,
        p {
          display: grid;
          -webkit-animation: fadein 0.2s ease-in-out; /* Safari, Chrome and Opera > 12.1 */
          -moz-animation: fadein 0.2s ease-in-out; /* Firefox < 16 */
          -ms-animation: fadein 0.2s ease-in-out; /* Internet Explorer */
          -o-animation: fadein 0.2s ease-in-out; /* Opera < 12.1 */
          animation: fadein 0.2s ease-in-out;
        }
      }
    `,
    CourseDataWrapper = styled("div")`
      grid-area: 1 / 1 / span 1 / span 1;
      height: 360px;
      padding: 50px;
      display: grid;
      grid-template-columns: 20% 80%;
      grid-template-rows: 32px 3fr 1fr;
      &:hover {
        cursor: pointer;
      }
    `,
    CourseThemeIconWrapper = styled("div")`
      display: flex;
      justify-content: center;
      align-items: flex-start;
      grid-area: 2 / 1 / span 1;
      margin-right: 35px;
    `,
    CourseTypeText = styled("p")`
      font-size: 1em;
      font-weight: normal;
      font-family: ${theme.fontFaces.headers};
      grid-area: 1 / 2 / span 1;
      display: flex;
      align-items: flex-start;
      color: ${theme.colors.black};
    `,
    CourseDetailWrapper = styled("div")`
      grid-area: 3 / 2 / span 1;
      display: flex;
      align-self: end;
      flex-direction: row;
      justify-content: left;
      p {
        border-top-width: 4px;
        border-top-style: solid;
        padding-top: 9px;
        margin-right: 30px;
        font-size: 13px;
        line-height: 16px;
        font-family: ${theme.fontFaces.headers};
        font-weight: bold;

        &:last-child {
          margin-right: 0px;
        }
      }
    `,
    CourseDescriptionWrapper = styled("div")`
      grid-area: 1 / 2 / span 1;
      display: grid;
      padding: 50px;
      grid-template: 32px 3fr 1fr / auto;
    `,
    CourseDescriptionText = styled("p")`
      grid-area: 2 / 1 / span 1 / span 1;
      color: ${theme.colors.white};
      font-size: 20px;
      font-weight: normal;
      line-height: 31px;
      display: none;
    `,
    CourseCtaWrapper = styled("div")`
      grid-area: 3 / 1 / span 1 / span 2;
      align-self: end;
      align-items: center;
      display: flex;
      flex-direction: row;
    `,
    CourseCtaIcon = styled("p")`
      padding-left: 3px;
      padding-bottom: 5px;
      display: none;
    `,
    CourseCtaText = styled("p")`
      color: ${darkImageOverlayText ? theme.colors.white : theme.colors.main};
      font-family: ${theme.fontFaces.headers};
      font-size: 13px;
      font-weight: bold;
      line-height: 31px;
      padding-bottom: 5px;
      display: none;
    `,
    type = determineProductType(courseType),
    to = cta?.url || determineProductPath(type, languageCode, slug);
  return (
    <>
      {courseImage && courseImage.gatsbyImageData && (
        <DoubleCardImageBg image={getImage(courseImage.gatsbyImageData)} />
      )}

      <DoubleCardWrapper
        className="mrkt-course-card secondary-background-hover"
        to={`${to}`}
      >
        <CourseDataWrapper>
          <CourseTypeText>{courseType}</CourseTypeText>
          <h2
            css={css`
              grid-area: 2 / 2 / span 1 / span 1;
              color: ${theme.colors.main};
              font-size: 1.8em;
              line-height: 42px;
              letter-spacing: -0.91px;
            `}
          >
            {title}
          </h2>
          <CourseDetailWrapper>
            {courseCost ? (
              <p className={`black-border main`}>{courseCost}</p>
            ) : null}
            {courseAvailability ? (
              <p className={`black-border main`}>{courseAvailability}</p>
            ) : null}
            {courseDuration ? (
              <p className={`black-border main`}>{courseDuration}</p>
            ) : null}
          </CourseDetailWrapper>
        </CourseDataWrapper>
        <CourseDescriptionWrapper>
          <CourseDescriptionText>
            {shortDescription.shortDescription}
          </CourseDescriptionText>
          <CourseCtaWrapper>
            <CourseCtaText>{cta?.linkText || "Enroll Now"}</CourseCtaText>
            <CourseCtaIcon>
              <RightGoToArrow fillColor={"main-fill"} />
            </CourseCtaIcon>
          </CourseCtaWrapper>
        </CourseDescriptionWrapper>
      </DoubleCardWrapper>
    </>
  );
};

DualColumnCard.propTypes = {
  slug: PropTypes.string,
  title: PropTypes.string,
  shortDescription: PropTypes.shape({ shortDescription: PropTypes.string }),
  courseCost: PropTypes.string,
  courseDuration: PropTypes.string,
  courseType: PropTypes.string,
  courseAvailability: PropTypes.string,
  courseImage: PropTypes.shape({
    fluid: PropTypes.shape({ srcSet: PropTypes.string }),
    description: PropTypes.string,
  }),
  gridColumnValue: PropTypes.string,
  backgroundColorClass: PropTypes.string,
  courseIcon: PropTypes.element,
  theme: PropTypes.shape({
    theme: PropTypes.string,
  }),
};

export default DualColumnCard;
